<template>
  <div class="ca-page">
    <!-- ===== 头部（复用全局 .customer-header 体系，与合同管理一致） ===== -->
    <div class="customer-header" ref="caHeader">
      <div class="customer-header__top">
        <div class="customer-header__left">
          <van-icon name="arrow-left" class="ca-back" @click="goBack" />
          <span class="customer-header__title">销售合同审核</span>
        </div>
        <div class="customer-header__search">
          <el-input v-model="keyword" class="search-input" placeholder="搜索合同名称" @change="onSearch">
            <i slot="prefix" class="el-icon-search"></i>
          </el-input>
        </div>
        <div class="customer-header__actions">
          <div class="customer-header__btn" :class="{'customer-header__btn--primary': batchMode}" @click="toggleBatch">
            <i class="el-icon-finished"></i>
            <span>{{ batchMode ? '取消批量' : '批量' }}</span>
          </div>
        </div>
      </div>

      <!-- 状态分段（待审核 / 全部） -->
      <div class="customer-header__tabs">
        <div v-for="(t, idx) in statusTabs" :key="idx"
             class="customer-header__tab"
             :class="{'customer-header__tab--active': curStatus === t.v}"
             @click="switchStatus(t.v)">
          {{ t.label }}<em class="ca-tab-num" v-if="t.v === '2' && pending > 0"> {{ pending }}</em>
        </div>
      </div>

      <!-- 批量模式：全选条 -->
      <div class="batch-bar" v-if="batchMode">
        <van-checkbox :value="allSelected" @input="toggleSelectAll">全选待审核</van-checkbox>
        <span class="batch-bar__count">已选 {{ selected.length }} 个</span>
      </div>
    </div>

    <div class="customer-header-spacer" :style="{ height: headerH + 'px' }"></div>

    <!-- 单合同上下文提示 -->
    <div class="ca-scope" v-if="orderSn">
      <van-icon name="info-o" />
      <span>仅显示本合同（{{ scopeName || orderSn }}）相关的审核</span>
    </div>

    <!-- ===== 列表 ===== -->
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <div class="list-box list-box--gap ca-list-box" :class="{'ca-list-box--batch': batchMode && selected.length > 0}">
        <van-list
          v-model:loading="loading"
          :finished="finished"
          finished-text="没有更多了"
          @load="onLoad">
          <div v-for="(row, idx) in list" :key="row.approval_sn + '-' + idx"
               class="crm-item order-item ca-item"
               :class="{'ca-item--batch': batchMode}">
            <div class="item-head customer-card order-card">
              <van-checkbox v-if="batchMode"
                            class="ca-item__check"
                            :value="isSelected(row.approval_sn)"
                            :disabled="!row.can_approve"
                            @input="v => onSelect(row.approval_sn, v)" />
              <div class="customer-card__main" @click="onCardClick(row)">
                <div class="customer-card__content">
                  <div class="customer-card__header">
                    <div class="customer-card__name-wrap">
                      <span class="customer-card__name">{{ row.order_name }}</span>
                    </div>
                    <span class="status-tag" :class="statusClass(row.status)">{{ row.status_name }}</span>
                  </div>
                  <div class="customer-card__row">提交人：{{ row.create_user_name || '-' }}</div>
                  <div class="customer-card__row customer-card__row--muted">
                    <span>提交：{{ (row.create_time || '').slice(0, 16) }}</span>
                    <span v-if="row.update_time">处理：{{ (row.update_time || '').slice(0, 16) }}</span>
                  </div>
                  <div class="customer-card__row customer-card__row--muted ca-desc">{{ row.task_descript }}</div>
                  <div class="customer-card__row ca-reject" v-if="row.status === 5 && row.mssage">
                    {{ row.mssage }}
                  </div>
                </div>
              </div>
              <!-- 单条操作（非批量态 + 待审核） -->
              <div class="ca-actions" v-if="!batchMode && row.can_approve">
                <van-button size="small" plain round type="danger" @click.stop="doOne(row, 'reject')">不通过</van-button>
                <van-button size="small" round type="primary" @click.stop="doOne(row, 'pass')">通过</van-button>
              </div>
            </div>
          </div>
        </van-list>
      </div>
    </van-pull-refresh>

    <!-- ===== 底部批量操作栏 ===== -->
    <div class="alloc-batch-bar" v-if="batchMode && selected.length > 0">
      <span class="alloc-batch-bar__count">已选 {{ selected.length }} 个</span>
      <div class="alloc-batch-bar__actions">
        <van-button type="primary" :loading="submitting" @click="doBatch('pass')">全部通过</van-button>
        <van-button type="danger" :loading="submitting" @click="doBatch('reject')">全部驳回</van-button>
      </div>
    </div>

    <!-- ===== 驳回原因弹层（必填） ===== -->
    <van-popup v-model="rejectShow" round position="bottom" class="ca-reject-pop">
      <div class="ca-reject-pop__body">
        <div class="ca-reject-pop__title">驳回审核</div>
        <div class="ca-reject-pop__tip">
          将对 <b>{{ rejectTargets.length }}</b> 条审核执行「不通过」，请填写原因
          <em>（将随通知发送给提交人）</em>
        </div>
        <van-field
          v-model="rejectRemark"
          type="textarea"
          rows="3"
          maxlength="100"
          show-word-limit
          placeholder="如：金额与凭证不符，请重新上传" />
        <div class="ca-reject-pop__actions">
          <van-button block round @click="rejectShow = false">取消</van-button>
          <van-button block round type="danger" :loading="submitting" @click="submitReject">确认驳回</van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { contractApprovalListData, contractApprovalBadgeData, contractApprovalDoData } from "../api/api.js";

export default {
  name: "ContractApprovalList",
  data() {
    return {
      /* 头部 */
      keyword: "",
      headerH: 150,
      /* 数据 */
      list: [],
      page: 1,
      limit: 20,
      total: 0,
      pending: 0,
      curStatus: "2",              // 默认「待审核」
      statusTabs: [
        { label: "待审核", v: "2" },
        { label: "全部", v: "all" }
      ],
      /* 列表状态 */
      loading: false,
      finished: false,
      refreshing: false,
      /* 批量 */
      batchMode: false,
      selected: [],
      /* 提交 */
      submitting: false,
      /* 驳回弹层 */
      rejectShow: false,
      rejectRemark: "",
      rejectTargets: [],
      /* 上下文（可选） */
      orderSn: "",
      scopeName: ""
    };
  },
  computed: {
    allSelected() {
      const sel = this.selected;
      const enabled = this.list.filter(r => r.can_approve).map(r => r.approval_sn);
      if (enabled.length === 0) return false;
      return enabled.every(sn => sel.indexOf(sn) > -1);
    }
  },
  created() {
    if (this.$route && this.$route.query) {
      this.orderSn = this.$route.query.order_sn || "";
    }
    this.fetchBadge();
    this.fetchList(true);
  },
  mounted() {
    this.$nextTick(() => this.measureHeader());
  },
  methods: {
    measureHeader() {
      const el = this.$refs.caHeader;
      if (el) { this.headerH = el.offsetHeight || 150; }
    },
    goBack() {
      if (window.history.length > 1) { this.$router.back(); }
      else { this.$router.push("/client"); }
    },
    statusClass(s) {
      if (s === 2) return "tag-wait";
      if (s === 3) return "tag-receive";
      if (s === 5) return "tag-danger";
      return "tag-allot";
    },
    /* ---------- 数据 ---------- */
    fetchBadge() {
      const params = {};
      if (this.orderSn) { params.order_sn = this.orderSn; }
      contractApprovalBadgeData(params).then(res => {
        if (res && res.code === 200) { this.pending = (res.data && res.data.pending) || 0; }
      }).catch(() => {});
    },
    fetchList(reset) {
      if (reset) { this.page = 1; this.finished = false; }
      this.loading = true;
      const params = { page: this.page, limit: this.limit, status: this.curStatus };
      if (this.orderSn) { params.order_sn = this.orderSn; }
      if (this.keyword) { params.keyword = this.keyword; }
      return contractApprovalListData(params).then(res => {
        if (res && res.code === 200) {
          const d = res.data || {};
          const rows = d.data || [];
          this.total = d.total || 0;
          this.pending = (typeof d.pending === "number") ? d.pending : this.pending;
          if (reset) { this.list = rows; } else { this.list = this.list.concat(rows); }
          if (rows.length < this.limit) { this.finished = true; }
          if (reset && this.list.length > 0) { this.scopeName = this.list[0].order_name; }
        } else {
          this.finished = true;
        }
      }).catch(() => {
        this.finished = true;
      }).finally(() => {
        this.loading = false;
        this.refreshing = false;
        this.$nextTick(() => this.measureHeader());
      });
    },
    onLoad() {
      if (this.finished) { return; }
      this.page += 1;
      this.fetchList(false);
    },
    onRefresh() {
      this.fetchBadge();
      this.fetchList(true);
    },
    onSearch() {
      this.fetchList(true);
    },
    switchStatus(v) {
      if (this.curStatus === v) { return; }
      this.curStatus = v;
      this.batchMode = false;
      this.selected = [];
      this.fetchList(true);
    },
    /* ---------- 选择 ---------- */
    toggleBatch() {
      this.batchMode = !this.batchMode;
      if (!this.batchMode) { this.selected = []; }
      this.$nextTick(() => this.measureHeader());
    },
    isSelected(sn) { return this.selected.indexOf(sn) > -1; },
    onSelect(sn, checked) {
      if (checked) {
        if (this.selected.indexOf(sn) < 0) { this.selected.push(sn); }
      } else {
        this.selected = this.selected.filter(s => s !== sn);
      }
      this.$nextTick(() => this.measureHeader());
    },
    toggleSelectAll(val) {
      if (val) {
        this.selected = this.list.filter(r => r.can_approve).map(r => r.approval_sn);
      } else {
        this.selected = [];
      }
      this.$nextTick(() => this.measureHeader());
    },
    onCardClick(row) {
      if (this.batchMode) {
        if (!row.can_approve) { this.$toast("该条已处理，不可选中"); return; }
        this.onSelect(row.approval_sn, !this.isSelected(row.approval_sn));
        return;
      }
      this.$router.push("/contractDetail/" + row.order_sn);
    },
    /* ---------- 操作 ---------- */
    afterDone(msg) {
      this.$toast(msg);
      this.batchMode = false;
      this.selected = [];
      this.fetchBadge();
      this.fetchList(true);
    },
    doOne(row, action) {
      if (this.submitting) { return; }
      if (action === "reject") {
        this.rejectTargets = [row.approval_sn];
        this.rejectRemark = "";
        this.rejectShow = true;
        return;
      }
      this.$dialog.confirm({
        title: "审核通过",
        message: "确定通过「" + row.order_name + "」的这条审核？"
      }).then(() => {
        this.submitting = true;
        contractApprovalDoData({ approval_sns: row.approval_sn, action: "pass" }).then(res => {
          if (res && res.code === 200) { this.afterDone("审核通过"); }
          else { this.$toast((res && res.msg) ? res.msg : "处理失败"); }
        }).catch(() => { this.$toast("网络错误，请重试"); })
          .finally(() => { this.submitting = false; });
      }).catch(() => {});
    },
    doBatch(action) {
      if (this.submitting) { return; }
      if (this.selected.length === 0) { this.$toast("请先选择要审核的记录"); return; }
      if (action === "reject") {
        this.rejectTargets = this.selected.slice();
        this.rejectRemark = "";
        this.rejectShow = true;
        return;
      }
      this.$dialog.confirm({
        title: "批量审核通过",
        message: "确定通过已选的 " + this.selected.length + " 条审核？"
      }).then(() => this.execBatch("pass", "")).catch(() => {});
    },
    submitReject() {
      const r = (this.rejectRemark || "").trim();
      if (!r) { this.$toast("请填写驳回原因"); return; }
      this.execBatch("reject", r);
    },
    execBatch(action, remark) {
      if (this.submitting) { return; }
      const sns = (this.rejectTargets && this.rejectTargets.length && action === "reject")
        ? this.rejectTargets.slice()
        : this.selected.slice();
      if (sns.length === 0) { this.$toast("请先选择要审核的记录"); return; }
      this.submitting = true;
      contractApprovalDoData({ approval_sns: sns.join(","), action: action, remark: remark || "" }).then(res => {
        if (res && res.code === 200) {
          const d = res.data || {};
          const failed = (d.fail || 0);
          this.rejectShow = false;
          if (failed > 0) {
            // 部分成功：保留失败项以便重试
            const failSns = (d.details || []).filter(x => x.status !== "ok").map(x => x.sn);
            this.selected = failSns;
            this.batchMode = failSns.length > 0;
            this.$toast("成功 " + (d.ok || 0) + " 条 · 失败 " + failed + " 条，可重试");
            this.fetchBadge();
            this.fetchList(true);
          } else {
            this.afterDone(action === "pass" ? ("已通过 " + (d.ok || 0) + " 条") : ("已驳回 " + (d.ok || 0) + " 条"));
          }
        } else {
          this.$toast((res && res.msg) ? res.msg : "处理失败");
        }
      }).catch(() => { this.$toast("网络错误，请重试"); })
        .finally(() => { this.submitting = false; });
    }
  }
};
</script>

<style scoped>
.ca-page { min-height: 100vh; background: #f5f7fb; padding-bottom: 80px; }
.ca-back { font-size: 18px; margin-right: 6px; color: #1d2129; }
.ca-tab-num { font-style: normal; color: #e34d59; font-weight: 700; }
.ca-scope {
  display: flex; align-items: center; gap: 6px;
  margin: 10px 12px 0; padding: 8px 12px;
  background: #eef2fb; color: #2b5fd9; font-size: 12.5px; border-radius: 8px;
}
.ca-list-box { padding: 10px 12px 0; }
.ca-list-box--batch { padding-bottom: 80px; }
.ca-item { position: relative; }
.ca-item--batch { padding-left: 34px; }
.ca-item__check { position: absolute; top: 16px; left: 8px; z-index: 3; }
.ca-desc { font-size: 12px; color: #8a93a5; line-height: 1.6; }
.ca-reject { font-size: 12.5px; color: #e34d59; }
.ca-actions { display: flex; justify-content: flex-end; gap: 8px; padding: 8px 12px 10px; }
.ca-reject-pop__body { padding: 16px 14px 18px; }
.ca-reject-pop__title { font-size: 16px; font-weight: 600; color: #1d2129; text-align: center; margin-bottom: 8px; }
.ca-reject-pop__tip { font-size: 12.5px; color: #4e5969; line-height: 1.7; margin-bottom: 10px; }
.ca-reject-pop__tip em { font-style: normal; color: #e34d59; }
.ca-reject-pop__actions { display: flex; gap: 10px; margin-top: 14px; }
/* 批量栏兜底样式（与 client.vue 同名同值，避免 chunk 未加载时丢样式） */
.batch-bar { display: flex; align-items: center; justify-content: space-between;
  padding: 8px 14px; background: #fff; border-bottom: 1px solid #eee; font-size: 13px; color: #4e5969; }
.batch-bar__count { color: #2b5fd9; font-weight: 600; }
.alloc-batch-bar { position: fixed; left: 0; right: 0; bottom: 50px; z-index: 50;
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 14px; background: #fff; box-shadow: 0 -2px 10px rgba(0,0,0,0.08); border-top: 1px solid #eee; }
.alloc-batch-bar__count { font-size: 13px; color: #4e5969; }
.alloc-batch-bar__actions { display: flex; gap: 10px; }
</style>



// WEBPACK FOOTER //
// src/pages/contractApprovalList.vue